import { CellChange, Row } from '@silevis/reactgrid'
import {
  IPickerList,
  IPickersMap,
  ITableCell,
  ITableItemsData,
  TAdditionalIconColIds,
  TAvailableColumnIds,
  TBulkErrorResponse,
  TCellInfo,
  TCellTypes,
  TErrorResponseTableData
} from './types'
import { IconCellInterface } from './Table/Cells/CustomCellIconTemplate'
import { ReactComponent as Warning16Icon } from 'assets/svg/WarningRed16.svg'
import { ReactComponent as Delete16Icon } from 'assets/svg/Delete16Red.svg'
import { TextCellInterface } from './Table/Cells/CustomCellTextTemplate'
import { defaultFocusPosition, defaultHeaderRowId } from './constants'
import {
  normalizeNumberValue,
  NumberCellInterface
} from './Table/Cells/CustomCellNumberTemplate'
import { SelectCellInterface } from './Table/Cells/CustomCellSelectTemplate'
import { ManualContractCreationRequestData } from '../ContractDetails/types'
import { convertDateToRequestFormat } from '../../utils/moment'
import moment from 'moment'
import { DatepickerCellInterface } from './Table/Cells/CustomCellDatepickerTemplate'
import {
  currencyParser,
  twoDecimalParser
} from '../ContractDetails/ContractDetailsGeneral/ContractDetailsForm/utils'
import { convertStringToCompareFormat } from '../../helper/common'
import { CustomHeaderCell } from './Table/Cells/CustomHeaderCellTemplate'
import { VENDOR_FEE_TYPES_VALUES } from '../ContractDetails/constants'
import { REBATE_CONDITIONS_TYPE } from '../ContractDetails/RebateConditions/RebateConditionsForm/constants'

export const getHeaderRow = (data: ITableCell[]): Row => ({
  rowId: defaultHeaderRowId,
  height: 40,
  cells: data.map((item) =>
    getHeaderCell(
      item.headerText ?? '',
      item.tooltipText ?? '',
      item.required ?? false
    )
  )
})

export const getHeaderCell = (
  text: string,
  tooltipText: string,
  required: boolean
): CustomHeaderCell => ({
  type: 'header',
  text,
  tooltipText,
  required
})

export const getCell = ({
  cell,
  row,
  rowId,
  focusChanged,
  errorCellIds,
  deletePressed,
  pickerItems
}: TCellInfo): TCellTypes => {
  const itemText = row[cell.columnId] ?? ''
  const isError = errorCellIds.includes(cell?.columnId)
  const generalVals = {
    type: cell.cellType,
    text: itemText,
    isError,
    focusCallback: () => focusChanged({ rowId, columnId: cell.columnId }),
    unFocusCallback: () => focusChanged(defaultFocusPosition)
  }
  switch (cell.cellType) {
    case 'dropdown':
      return {
        values: pickerItems,
        disabled: pickerItems === null,
        ...generalVals
      } as SelectCellInterface
    case 'icon':
      return getIconData({
        deletePressed,
        isError,
        columnId: cell.columnId as TAdditionalIconColIds,
        rowId
      })
    case 'date':
      return {
        value: itemText,
        placeholder: cell.placeholder,
        ...generalVals
      } as DatepickerCellInterface
    case 'number': {
      const adminFee = cell.columnId === 'admin_fee'
      const percentage = Boolean(
        cell.columnId === 'rebate_amount' || cell.columnId === 'threshold'
      )

      let max: string | undefined = undefined
      if (adminFee) {
        max = '3'
      } else if (percentage) {
        max = '100'
      }

      const minFields = ['baseline_spend', 'rebate_amount', 'threshold']
      const min = minFields.includes(cell.columnId) ? '0.01' : undefined
      const numValue = normalizeNumberValue(itemText, min)

      return {
        ...generalVals,
        value: numValue,
        text: Number.isNaN(numValue) ? '' : numValue.toString(),
        placeholder: cell.placeholder,
        max,
        min,
        parser: adminFee || percentage ? twoDecimalParser : currencyParser,
        disabled:
          ['admin_fee', 'rebate_amount', 'threshold', 'baseline_spend'].indexOf(
            cell.columnId
          ) < 0
      } as NumberCellInterface
    }
    case 'text':
    default:
      return {
        value: itemText,
        placeholder: cell.placeholder,
        nonEditable: cell.nonEditable,
        ...generalVals
      } as TextCellInterface
  }
}

const getIconData = ({
  columnId,
  deletePressed,
  rowId,
  isError
}: Pick<TCellInfo, 'deletePressed' | 'rowId'> & {
  isError: boolean
  columnId: TAdditionalIconColIds
}): IconCellInterface => {
  switch (columnId) {
    case 'deleteIcon':
      return {
        type: 'icon',
        iconSrc: deletePressed && <Delete16Icon />,
        onIconClick: deletePressed ? () => deletePressed(rowId) : undefined
      } as IconCellInterface
    case 'icon':
      return {
        type: 'icon',
        iconSrc: isError && <Warning16Icon />
      } as IconCellInterface
  }
}

export const getUpdatedRowInfo = (
  changes: CellChange<TCellTypes>[]
): Partial<ITableItemsData> => {
  return changes.reduce((prev, item) => {
    let value: string | number | null
    switch (item.newCell.type) {
      case 'dropdown':
      case 'date':
        value = item.newCell['text']
        break
      case 'number': {
        const numberCell = item.newCell as NumberCellInterface
        const numVal = normalizeNumberValue(numberCell.value, numberCell.min)
        value = Number.isNaN(numVal) ? null : numVal
        break
      }
      case 'text':
        value = item.newCell.text
        break
      default:
        value = ''
    }
    return {
      ...prev,
      [item.columnId]: value
    }
  }, {})
}

const _compareValues = (
  value1: ITableItemsData[keyof ITableItemsData] | string,
  value2: ITableItemsData[keyof ITableItemsData] | string
) =>
  convertStringToCompareFormat(String(value1)) ===
  convertStringToCompareFormat(String(value2))

// parsing value got from selection or copy-pasting items into the table
export const parseFieldValue = (
  items: Partial<ITableItemsData>,
  getPickerItems: (columnId: TAvailableColumnIds) => IPickerList[] | null
): Partial<ITableItemsData> => {
  const updatedItems = {}
  Object.keys(items).forEach((key) => {
    switch (key) {
      case 'vendor':
      case 'vendor_contract_steward':
      case 'contract_category':
      case 'led_by':
      case 'vendor_fee_type':
        updatedItems[key] =
          (getPickerItems(key) ?? []).find(
            (item) =>
              _compareValues(item.label, items[key]) ||
              _compareValues(item.value, items[key])
          )?.value ?? null
        break
      case 'finish_date':
      case 'start_date':
        updatedItems[key] =
          items[key] && moment(items[key]).isValid() ? moment(items[key]) : null
        break
      case 'admin_fee': {
        updatedItems[key] = null
        if (items[key]) {
          if (Number(items[key]) > 3) {
            updatedItems[key] = 3
          } else if (Number(items[key]) < 1) {
            updatedItems[key] = 1
          } else {
            updatedItems[key] = items[key]?.toFixed(0)
          }
        }
        break
      }
      default:
        updatedItems[key] = items[key]
    }
  })
  return updatedItems
}

export const parseTableDataToRequest = (
  data: ITableItemsData[]
): Partial<ManualContractCreationRequestData>[] =>
  data.map((item) => ({
    organization_contract_category: item.led_by ?? '',
    vendor_contract_steward: item.vendor_contract_steward,
    vendor: item.vendor,
    start_date: item.start_date
      ? convertDateToRequestFormat(item.start_date)
      : '',
    primary_finish_date: item.finish_date
      ? convertDateToRequestFormat(item.finish_date)
      : '',
    admin_fee: item.admin_fee ?? undefined,
    vendor_fee_type: item.vendor_fee_type,
    rebate_type: item.rebate_type,
    baseline_spend: item.baseline_spend ?? undefined,
    rebate_amount: item.rebate_amount ?? undefined,
    threshold: item.threshold ?? undefined
  }))

export const parseErrorResponse = (
  data: TBulkErrorResponse
): TErrorResponseTableData => {
  return (data.contracts ?? []).reduce((acc, item, idx) => {
    const errors: TAvailableColumnIds[] = []
    const keys = Object.keys(item)
    if (keys.length) {
      errors.push('icon')
      Object.keys(item).forEach((key) => {
        switch (key) {
          case 'organization_contract_category': {
            errors.push('contract_category')
            errors.push('led_by')
            break
          }
          case 'primary_finish_date':
            errors.push('finish_date')
            break
          default:
            errors.push(key)
        }
      })
      return {
        ...acc,
        [idx]: errors
      }
    }
    return acc
  }, {} as TErrorResponseTableData)
}

const checkAdminFee = (
  item: ITableItemsData,
  errors: TAvailableColumnIds[]
) => {
  if (
    item.vendor_fee_type !== VENDOR_FEE_TYPES_VALUES.ADMIN &&
    item.admin_fee
  ) {
    errors.push('admin_fee')
    errors.push('vendor_fee_type')
  }
  if (
    item.vendor_fee_type === VENDOR_FEE_TYPES_VALUES.ADMIN &&
    !item.admin_fee
  ) {
    errors.push('admin_fee')
    errors.push('vendor_fee_type')
  }
}

const checkBaselineSpend = (
  item: ITableItemsData,
  errors: TAvailableColumnIds[]
) => {
  if (
    item.rebate_type &&
    item.rebate_type !== REBATE_CONDITIONS_TYPE.GROWTH_REBATE &&
    item.baseline_spend
  ) {
    errors.push('baseline_spend')
    errors.push('rebate_type')
  }
  if (
    item.rebate_type === REBATE_CONDITIONS_TYPE.GROWTH_REBATE &&
    item.rebate_amount &&
    !item.baseline_spend
  ) {
    errors.push('baseline_spend')
  }
}

const checkRebateAmount = (
  item: ITableItemsData,
  errors: TAvailableColumnIds[]
) => {
  if (
    item.rebate_type === REBATE_CONDITIONS_TYPE.NO_REBATE &&
    item.rebate_amount
  ) {
    errors.push('rebate_amount')
    errors.push('rebate_type')
  }
  if (
    item.rebate_type &&
    item.rebate_type === REBATE_CONDITIONS_TYPE.GROWTH_REBATE &&
    item.baseline_spend &&
    !item.rebate_amount
  ) {
    errors.push('rebate_amount')
  }
  if (
    item.rebate_type &&
    item.rebate_type === REBATE_CONDITIONS_TYPE.MARKET_SHARE_REBATE &&
    item.threshold &&
    !item.rebate_amount
  ) {
    errors.push('rebate_amount')
  }
}

const checkThreshold = (
  item: ITableItemsData,
  errors: TAvailableColumnIds[]
) => {
  if (
    item.rebate_type &&
    item.rebate_type !== REBATE_CONDITIONS_TYPE.MARKET_SHARE_REBATE &&
    item.threshold
  ) {
    errors.push('threshold')
    errors.push('rebate_type')
  }
  if (
    item.rebate_type === REBATE_CONDITIONS_TYPE.MARKET_SHARE_REBATE &&
    item.rebate_amount &&
    !item.threshold
  ) {
    errors.push('threshold')
  }
}

export const checkMissingFieldsValidation = (
  data: ITableItemsData[]
): TErrorResponseTableData => {
  return data.reduce((acc, item, idx) => {
    const errors: TAvailableColumnIds[] = []
    Object.keys(item).forEach((key) => {
      switch (key) {
        // these fields are required to proceed
        case 'vendor':
        case 'contract_category':
        case 'led_by':
        case 'start_date':
        case 'finish_date':
        case 'rebate_type':
          if (!item[key]) {
            errors.push(key)
          }
          break
        case 'admin_fee': {
          checkAdminFee(item, errors)
          break
        }
        case 'baseline_spend': {
          checkBaselineSpend(item, errors)
          break
        }
        case 'rebate_amount': {
          checkRebateAmount(item, errors)
          break
        }
        case 'threshold': {
          checkThreshold(item, errors)
          break
        }
      }
    })
    if (errors.length) {
      errors.push('icon')
      return {
        ...acc,
        [idx]: errors
      }
    }
    return acc
  }, {} as TErrorResponseTableData)
}

export const updateRowIdsOnDelete = (
  data: IPickersMap | TErrorResponseTableData,
  rowId: string | number
): IPickersMap | TErrorResponseTableData => {
  return Object.keys(data).reduce((acc, key) => {
    const idx = Number(key)
    if (idx === rowId) {
      return acc
    } else if (idx < Number(rowId)) {
      return {
        ...acc,
        [idx]: data[key]
      }
    } else {
      return {
        ...acc,
        [idx - 1]: data[key]
      }
    }
  }, {})
}
